import React from 'react';
import { Controller, UseFormSetValue } from 'react-hook-form';
import { CollaboratorFormContext } from "@/features/Collaborators/Edit/Contexts/CollaboratorFormContext";
import { collaboratorSchemaHelpers } from '@/features/Collaborators/Models/collaborator';
import { CurrencyInput } from '@/components/shared';

interface BenefitsFieldsetProps {
    context: CollaboratorFormContext;
    control: any;
    setValue: UseFormSetValue<any>;
    watch: any;
    errors?: Record<string, any>;
    isSubmitting?: boolean;
    layout?: 'full' | 'compact';
}

const BenefitsFieldset: React.FC<BenefitsFieldsetProps> = ({
    context,
    control,
    setValue,
    watch,
    errors = {},
    isSubmitting = false,
    layout = 'full'
}) => {
    // Helper para verificar se campo deve aparecer
    const shouldShow = (fieldName: string) => collaboratorSchemaHelpers.shouldShowField(fieldName, context);

    // Acessar dados da família via React Hook Form
    const spouseName = watch('spouseName');
    const children = watch('children') || [];

    // Construir lista de dependentes potenciais
    const potentialDependents = React.useMemo(() => {
        const dependents: Array<{ name: string; relationship: string }> = [];

        // Adicionar cônjuge se existir
        if (spouseName && spouseName.trim()) {
            dependents.push({
                name: spouseName.trim(),
                relationship: 'Cônjuge'
            });
        }

        // Adicionar filhos
        children.forEach((child: any) => {
            if (child.name && child.name.trim()) {
                dependents.push({
                    name: child.name.trim(),
                    relationship: 'Filho(a)'
                });
            }
        });

        return dependents;
    }, [spouseName, children]);

    // Layout contextual - seguindo padrão existente no projeto
    const getLayoutConfig = () => {
        if (layout === 'compact' || context === 'DP_UPDATE') {
            return {
                layout: 'compact' as const,
                fieldsetStyle: 'border p-4 rounded-md',
                legendStyle: 'text-sm font-medium px-1 text-gray-400',
                gridCols: 'grid-cols-1 md:grid-cols-2' // Layout mais compacto para modal
            };
        }
        return {
            layout: 'full' as const,
            fieldsetStyle: 'border p-4 rounded-md',
            legendStyle: 'text-sm font-medium px-1 text-gray-400',
            gridCols: 'grid-cols-2 md:grid-cols-4' // Layout padrão
        };
    };

    const finalLayout = getLayoutConfig();

    // Verificar se algum campo deve ser mostrado
    const hasVisibleFields = shouldShow('hasCajuBenefit') || shouldShow('hasWellhubBenefit') ||
        shouldShow('hasGuapecoBenefit') || shouldShow('transportationBenefitAmount') ||
        shouldShow('hasZeloBenefit');

    if (!hasVisibleFields) {
        return null;
    }

    // Valores atuais do watch
    const hasZeloBenefit = watch('hasZeloBenefit');

    return (
        <fieldset className={finalLayout.fieldsetStyle}>
            <legend className={finalLayout.legendStyle}>Benefícios</legend>
            <div className="space-y-4 pt-2">
                {/* Grid de benefícios básicos */}
                <div className={`grid gap-4 items-start ${finalLayout.gridCols}`}>
                    {/* Caju */}
                    {shouldShow('hasCajuBenefit') && (
                        <div className="flex items-center pt-5">
                            <Controller
                                name="hasCajuBenefit"
                                control={control}
                                render={({ field }) => (
                                    <input
                                        {...field}
                                        type="checkbox"
                                        id="hasCajuBenefit"
                                        checked={field.value || false}
                                        onChange={(e) => field.onChange(e.target.checked)}
                                        disabled={isSubmitting}
                                        className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                        data-testid="caju-benefit-checkbox"
                                    />
                                )}
                            />
                            <label htmlFor="hasCajuBenefit" className="ml-2 block text-sm text-gray-900">
                                Caju
                            </label>
                        </div>
                    )}

                    {/* Wellhub */}
                    {shouldShow('hasWellhubBenefit') && (
                        <div className="flex items-center pt-5">
                            <Controller
                                name="hasWellhubBenefit"
                                control={control}
                                render={({ field }) => (
                                    <input
                                        {...field}
                                        type="checkbox"
                                        id="hasWellhubBenefit"
                                        checked={field.value || false}
                                        onChange={(e) => field.onChange(e.target.checked)}
                                        disabled={isSubmitting}
                                        className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                        data-testid="wellhub-benefit-checkbox"
                                    />
                                )}
                            />
                            <label htmlFor="hasWellhubBenefit" className="ml-2 block text-sm text-gray-900">
                                Wellhub (Gympass)
                            </label>
                        </div>
                    )}

                    {/* Guapeco */}
                    {shouldShow('hasGuapecoBenefit') && (
                        <div className="flex items-center pt-5">
                            <Controller
                                name="hasGuapecoBenefit"
                                control={control}
                                render={({ field }) => (
                                    <input
                                        {...field}
                                        type="checkbox"
                                        id="hasGuapecoBenefit"
                                        checked={field.value || false}
                                        onChange={(e) => field.onChange(e.target.checked)}
                                        disabled={isSubmitting}
                                        className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                        data-testid="guapeco-benefit-checkbox"
                                    />
                                )}
                            />
                            <label htmlFor="hasGuapecoBenefit" className="ml-2 block text-sm text-gray-900">
                                Guapeco
                            </label>
                        </div>
                    )}

                    {/* Vale Transporte */}
                    {shouldShow('transportationBenefitAmount') && (
                        <div>
                            <Controller
                                name="transportationBenefitAmount"
                                control={control}
                                render={({ field }) => (
                                    <CurrencyInput
                                        id="transportationBenefitAmount"
                                        name="transportationBenefitAmount"
                                        label="Vale Transporte"
                                        value={field.value || ''}
                                        onValueChange={(value, name, values) => {
                                            field.onChange(values?.float || null);
                                        }}
                                        disabled={isSubmitting}
                                        placeholder="0,00"
                                        prefix="R$"
                                        allowNegativeValue={false}
                                        decimalsLimit={2}
                                        data-testid="transportation-benefit-input"
                                        error={errors.transportationBenefitAmount}
                                    />
                                )}
                            />
                        </div>
                    )}
                </div>

                {/* Zelo (Plano Funerário) */}
                {shouldShow('hasZeloBenefit') && (
                    <div className="border-t pt-4">
                        <div className="flex items-center">
                            <Controller
                                name="hasZeloBenefit"
                                control={control}
                                render={({ field }) => (
                                    <input
                                        {...field}
                                        type="checkbox"
                                        id="hasZeloBenefit"
                                        checked={field.value || false}
                                        onChange={(e) => field.onChange(e.target.checked)}
                                        disabled={isSubmitting}
                                        className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                        data-testid="zelo-benefit-checkbox"
                                    />
                                )}
                            />
                            <label htmlFor="hasZeloBenefit" className="ml-2 block text-sm text-gray-900">
                                Zelo (Plano Funerário)
                            </label>
                        </div>

                        {hasZeloBenefit && (
                            <div className="mt-4 pl-6">
                                <h4 className="text-sm font-medium text-gray-700">Incluir dependentes no Zelo:</h4>
                                {potentialDependents.length > 0 ? (
                                    <div className="mt-2 space-y-2">
                                        {potentialDependents.map(dep => (
                                            <div key={dep.name} className="flex items-center">
                                                <Controller
                                                    name="zeloDependents"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <input
                                                            type="checkbox"
                                                            id={`zelo-${dep.name}`}
                                                            checked={(field.value || []).includes(dep.name)}
                                                            onChange={(e) => {
                                                                const currentDependents = field.value || [];
                                                                const newDependents = e.target.checked
                                                                    ? [...currentDependents, dep.name]
                                                                    : currentDependents.filter((name: string) => name !== dep.name);
                                                                field.onChange(newDependents);
                                                            }}
                                                            disabled={isSubmitting}
                                                            className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                                        />
                                                    )}
                                                />
                                                <label htmlFor={`zelo-${dep.name}`} className="ml-2 text-sm text-gray-900">
                                                    {dep.name} <span className="text-gray-500">({dep.relationship})</span>
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-sm text-gray-500 mt-2">
                                        Nenhum dependente (cônjuge ou filhos) cadastrado na aba "Família".
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </fieldset>
    );
};

export default BenefitsFieldset; 